import React, { createContext, useEffect, useState, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { backend_url as backendUrl } from '../config/variables';
import axios from 'axios';
import makeAuthenticatedRequest from '../api';

export const UserContext = createContext();

export const UserProvider = (props) => {
  const [user, setUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [tableConfigs, setTableConfigs] = useState({});
  const navigate = useNavigate();

  // Load user + projects + table configs on mount
  useEffect(() => {
    initializeUser();
  }, []);

  const publicRoutes = [
    '/auth/login',
    '/auth/register',
    '/auth/request-password-reset',
    '/auth/new-password',
    '/auth/password-updated',
    '/auth/mail-sent',
    '/quickdownload',
    '/quickdownload/view',
    '/auth/404',
    '/download/report/:caseid',
    '/download/billing/invoice/:id',
    '/download/billing/quote/:id',
    '/download/billing/contract/:id',
  ];

  const fetchUserProjects = useCallback(async (userData) => {
    try {
      const res = await makeAuthenticatedRequest(
        `clients/${userData.client.id}/users/${userData.id}/projects`,
        null,
        { method:'get' }
      );
      setUserProjects(res.data.projects || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const refreshProjects = useCallback(() => {
    if (user) fetchUserProjects(user);
  }, [user, fetchUserProjects]);



  // NEW: call this after a successful login
  const login = (accessToken, refreshToken) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('successStatus', 'true');
  
    const info = jwtDecode(accessToken);
    const userData = { ...info, id: info.sub };
    setUser(userData);
    fetchUserProjects(userData);
    loadTableConfigs(userData.id);
  };

  // NEW: call this when the user clicks “logout”
  const logout = () => {
    localStorage.clear();
    setUser(null);
    setUserProjects([]);
    setTableConfigs({});
    navigate('/auth/login');
  };

  const initializeUser = () => {
    const token = localStorage.getItem('token');
    const success = localStorage.getItem('successStatus');

    if (success === 'true' && token) {
      const info = jwtDecode(token);
      if (info?.sub) {
        const userData = { ...info, id: info.sub };
        setUser(userData);
        fetchUserProjects(userData);
        loadTableConfigs(userData.id);
      }
    } else {
      const url = new URL(window.location.href);
      const pathname = url.pathname;
      const searchParams = url.searchParams;
      if (pathname === '/auth/new-password' && searchParams.has('token')) {
        return;
      }
      if (!publicRoutes.includes(pathname)) {
        localStorage.clear();
        navigate('/auth/login');
        window.location.reload();
      }
    }
  };

  // Persist and load table column configurations per user
  const loadTableConfigs = (userId) => {
    const raw = localStorage.getItem(`tableConfigs:${userId}`);
    if (raw) {
      try {
        setTableConfigs(JSON.parse(raw));
      } catch (_) {
        setTableConfigs({});
      }
    }
  };

  const saveTableConfig = (viewKey, columns) => {
    setTableConfigs(prev => {
      const updated = { ...prev, [viewKey]: columns };
      if (user?.id) {
        localStorage.setItem(
          `tableConfigs:${user.id}`,
          JSON.stringify(updated)
        );
      }
      return updated;
    });
  };

  const getTableConfig = (viewKey, defaultColumns = []) => {
    return tableConfigs[viewKey] || defaultColumns;
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post(
        `${backendUrl}/refresh`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('refreshToken')}` } }
      );
      localStorage.setItem('token', response.data.access_token);
    } catch (error) {
      console.error('Error refreshing token:', error);
      localStorage.clear();
      navigate('/auth/login');
    }
  };

  const createProject = async (projectData) => {
    try {
      const res = await makeAuthenticatedRequest(
        `clients/${user.client.id}/projects`,
        () => {},                // your refreshToken wrapper
        { method: 'post', data: projectData }
      );
      // append the newly created project (which is already assigned to current_user)
      setUserProjects(prev => [...prev, res.data.project]);
    } catch (e) {
      console.error('Error creating project', e);
    }
  };

  const updateProject = async (projectId, updatedData) => {
    try {
      const res = await makeAuthenticatedRequest(
        `clients/${user.client.id}/projects/${projectId}`,
        () => {},
        { method: 'put', data: updatedData }
      );
      setUserProjects(prev =>
        prev.map(p => p.id === projectId ? res.data.project : p)
      );
    } catch (e) {
      console.error('Error updating project', e);
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await makeAuthenticatedRequest(
        `clients/${user.client.id}/projects/${projectId}`,
        () => {},
        { method: 'delete' }
      );
      setUserProjects(prev =>
        prev.filter(p => p.id !== projectId)
      );
    } catch (e) {
      console.error('Error deleting project', e);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        userProjects,
        refreshProjects,
        setUser,
        refreshToken,
        getTableConfig,
        saveTableConfig,
        createProject,
        updateProject,
        deleteProject,
        login,
        logout
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};
